// One event, four tabs — /events/:eventId?tab=detail|stok|akses|promo.
//
// Detail: the facts and the edit dialog. Stok: allocations per lot (the old
// /events/:eventId/stock page, which now redirects here). Akses: who may see
// the event. Promo: the deals on it. Lifecycle actions (close, archive, void)
// stay on the events list, where their dialogs already live.

import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CalendarRange, MapPin, Pencil, Info, Package, Users, Gift, Receipt, TrendingUp } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { Segmented } from '@/components/ui/Segmented';
import { LoadingState } from '@/components/ui/LoadingState';
import { EmptyState } from '@/components/ui/EmptyState';
import { EventForm } from '@/components/events/EventForm';
import { EventStockPanel } from '@/components/events/EventStockPanel';
import { EventAccessSection } from '@/components/events/EventAccessSection';
import { PromoManager } from '@/components/events/PromoManager';
import { useEvent } from '@/hooks/useEvents';
import { usePermissions } from '@/hooks/usePermissions';
import { eventColorClasses } from '@/lib/eventColors';
import { formatDateTime, formatIDR, formatNumber, formatRelative } from '@/lib/format';
import type { EventStatus } from '@/types/domain';

type Tab = 'detail' | 'stok' | 'akses' | 'promo';
const TABS: Tab[] = ['detail', 'stok', 'akses', 'promo'];

const STATUS_LABEL: Record<EventStatus, string> = { active: 'Active', closed: 'Closed', archived: 'Arsip', void: 'Void' };
const STATUS_VARIANT: Record<EventStatus, 'active' | 'default' | 'warning' | 'danger'> = {
  active: 'active', closed: 'default', archived: 'warning', void: 'danger',
};

export default function EventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { can } = usePermissions();
  const { data: event, isLoading } = useEvent(eventId ?? null);
  const [editOpen, setEditOpen] = useState(false);

  const tab: Tab = useMemo(() => {
    const t = params.get('tab');
    return (TABS as string[]).includes(t ?? '') ? (t as Tab) : 'detail';
  }, [params]);
  const setTab = (t: Tab) => setParams(t === 'detail' ? {} : { tab: t }, { replace: true });

  const canEdit = can('events', 'edit');
  const showMoney = can('reports', 'view');

  if (isLoading) return <LoadingState message="Memuat event…" />;
  if (!event) {
    return (
      <EmptyState
        title="Event tidak ditemukan"
        description="Event ini mungkin sudah dihapus, atau linknya salah."
        action={<Button variant="primary" onClick={() => navigate('/events')}>Kembali ke Events</Button>}
      />
    );
  }

  const color = eventColorClasses(event.color);

  return (
    <>
      <PageHeader
        overline={`${event.code}${event.status !== 'active' ? ` · ${STATUS_LABEL[event.status]}` : ''}`}
        title={
          <span className="inline-flex items-center gap-2">
            <span className={`w-3 h-3 rounded-full ${color.dot}`} aria-hidden />
            {event.name}
          </span>
        }
        description={event.location ?? undefined}
        actions={
          <>
            {canEdit && (
              <Button variant="secondary" leftIcon={<Pencil size={16} />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            <Button variant="ghost" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate('/events')}>
              Events
            </Button>
          </>
        }
      />

      <div className="mb-4" data-tour="event-tabs">
        <Segmented<Tab>
          value={tab}
          onChange={setTab}
          role="tabs"
          aria-label="Bagian event"
          options={[
            { value: 'detail', label: 'Detail', icon: <Info size={14} /> },
            { value: 'stok', label: 'Stok', icon: <Package size={14} /> },
            { value: 'akses', label: 'Akses', icon: <Users size={14} /> },
            { value: 'promo', label: 'Promo', icon: <Gift size={14} /> },
          ]}
        />
      </div>

      {tab === 'detail' && (
        <div className="grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Chip variant={STATUS_VARIANT[event.status]}>{STATUS_LABEL[event.status]}</Chip>
              <code className="text-caption font-mono text-ink-subtle">{event.code}</code>
            </div>
            <dl className="space-y-2 text-small">
              <div className="flex items-start gap-2">
                <dt className="text-ink-subtle inline-flex items-center gap-1.5 w-28 shrink-0"><CalendarRange size={13} /> Mulai</dt>
                <dd className="text-ink">{formatDateTime(event.startAt)} <span className="text-ink-subtle">· {formatRelative(event.startAt)}</span></dd>
              </div>
              <div className="flex items-start gap-2">
                <dt className="text-ink-subtle inline-flex items-center gap-1.5 w-28 shrink-0"><CalendarRange size={13} /> Selesai</dt>
                <dd className="text-ink">{event.endAt ? formatDateTime(event.endAt) : <span className="text-ink-subtle">belum diisi</span>}</dd>
              </div>
              <div className="flex items-start gap-2">
                <dt className="text-ink-subtle inline-flex items-center gap-1.5 w-28 shrink-0"><MapPin size={13} /> Lokasi</dt>
                <dd className="text-ink">{event.location || <span className="text-ink-subtle">—</span>}</dd>
              </div>
            </dl>
            <p className="text-caption text-ink-subtle mt-4">
              Tutup, arsipkan, atau batalkan event dari menu ⋯ di daftar event.
            </p>
          </Card>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <Card>
              <p className="text-caption text-ink-subtle inline-flex items-center gap-1.5"><Receipt size={12} /> Transaksi</p>
              <p className="text-title font-display font-semibold text-ink tabular-nums mt-1">{formatNumber(event.totalTransactions)}</p>
            </Card>
            {showMoney && (
              <Card>
                <p className="text-caption text-ink-subtle inline-flex items-center gap-1.5"><TrendingUp size={12} /> Penjualan</p>
                <p className="text-title font-display font-semibold text-ink tabular-nums mt-1">{formatIDR(event.totalSales)}</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {tab === 'stok' && <EventStockPanel event={event} />}

      {tab === 'akses' && (
        <Card>
          <EventAccessSection eventId={event.id} />
        </Card>
      )}

      {tab === 'promo' && <PromoManager eventId={event.id} editable={canEdit} />}

      {editOpen && <EventForm open editingEventId={event.id} onClose={() => setEditOpen(false)} />}
    </>
  );
}
